/* Techniques header behaviour */
class TcHeader extends HTMLElement {
  connectedCallback() {
    this.overlay = this.dataset.overlay === 'true';
    this.panels = [...this.querySelectorAll('[data-tc-panel]')];
    this.triggers = [...this.querySelectorAll('[data-tc-panel-trigger]')];
    this.scrim = this.querySelector('[data-tc-scrim]');
    this.drawer = this.querySelector('.tc-drawer');
    this.search = this.querySelector('.tc-search');
    this.lastY = window.scrollY;
    this.setHeight();
    new ResizeObserver(() => this.setHeight()).observe(this);
    window.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.onScroll();
    this.initAnnouncements();
    this.initPanels();
    this.initDrawer();
    this.initSearch();
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.closeAll(); });
  }
  setHeight() {
    const h = this.querySelector('.tc-bar').offsetHeight + (this.querySelector('.tc-ann')?.offsetHeight || 0);
    document.documentElement.style.setProperty('--tc-header-h', h + 'px');
    document.documentElement.style.setProperty('--header-height', this.querySelector('.tc-bar').offsetHeight + 'px');
  }
  onScroll() {
    const y = window.scrollY;
    const scrolled = y > 40;
    this.classList.toggle('is-scrolled', scrolled);
    this.classList.toggle('is-solid', !this.overlay || y > window.innerHeight * 0.55 || this.classList.contains('has-panel'));
    const goingDown = y > this.lastY + 4;
    const goingUp = y < this.lastY - 4;
    if (goingDown && y > 320 && !this.classList.contains('has-panel')) this.classList.add('is-hidden');
    if (goingUp || y < 120) this.classList.remove('is-hidden');
    this.lastY = y;
  }
  initAnnouncements() {
    const msgs = [...this.querySelectorAll('.tc-ann__msg')];
    if (msgs.length < 2 || matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let i = 0;
    setInterval(() => { msgs[i].classList.remove('is-active'); i = (i + 1) % msgs.length; msgs[i].classList.add('is-active'); }, 4200);
  }
  openPanel(id) {
    this.panels.forEach((p) => {
      const on = p.dataset.tcPanel === String(id);
      p.classList.toggle('is-open', on); p.hidden = !on;
    });
    this.triggers.forEach((t) => {
      const on = t.dataset.tcPanelTrigger === String(id);
      t.setAttribute('aria-expanded', on); t.parentElement.classList.toggle('is-open', on);
    });
    this.classList.add('has-panel', 'is-solid');
    this.scrim.hidden = false; requestAnimationFrame(() => this.scrim.classList.add('is-on'));
  }
  closePanels() {
    this.panels.forEach((p) => { p.classList.remove('is-open'); setTimeout(() => { if (!p.classList.contains('is-open')) p.hidden = true; }, 550); });
    this.triggers.forEach((t) => { t.setAttribute('aria-expanded', 'false'); t.parentElement.classList.remove('is-open'); });
    this.classList.remove('has-panel');
    this.scrim.classList.remove('is-on'); setTimeout(() => { if (!this.scrim.classList.contains('is-on')) this.scrim.hidden = true; }, 400);
    this.onScroll();
  }
  initPanels() {
    let closeTimer;
    const hoverable = matchMedia('(hover: hover)').matches;
    this.triggers.forEach((t) => {
      const id = t.dataset.tcPanelTrigger;
      if (hoverable) {
        t.parentElement.addEventListener('mouseenter', () => { clearTimeout(closeTimer); this.openPanel(id); });
        t.parentElement.addEventListener('mouseleave', () => { closeTimer = setTimeout(() => this.closePanels(), 180); });
      }
      t.addEventListener('click', (e) => {
        if (t.getAttribute('aria-expanded') === 'true' && hoverable) return; // second click follows the link
        e.preventDefault();
        t.getAttribute('aria-expanded') === 'true' ? this.closePanels() : this.openPanel(id);
      });
    });
    this.querySelectorAll('.tc-nav__item:not(.has-panel)').forEach((li) => li.addEventListener('mouseenter', () => this.closePanels()));
    this.panels.forEach((p) => {
      p.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      p.addEventListener('mouseleave', () => { closeTimer = setTimeout(() => this.closePanels(), 180); });
      const media = p.querySelector('.tc-panel__media');
      p.querySelectorAll('[data-tc-preview]').forEach((a) => {
        const show = () => {
          media.classList.add('is-previewing');
          p.querySelectorAll('[data-tc-preview-card]').forEach((c) => c.classList.toggle('is-active', c.dataset.tcPreviewCard === a.dataset.tcPreview));
        };
        a.addEventListener('mouseenter', show); a.addEventListener('focus', show);
      });
      p.querySelector('ul').addEventListener('mouseleave', () => media.classList.remove('is-previewing'));
    });
    this.scrim.addEventListener('click', () => this.closeAll());
  }
  initDrawer() {
    const open = this.querySelector('[data-tc-drawer-open]');
    const close = this.querySelector('[data-tc-drawer-close]');
    open.addEventListener('click', () => {
      this.closeSearch(false); this.closePanels();
      this.drawer.inert = false;
      this.drawer.hidden = false; requestAnimationFrame(() => this.drawer.classList.add('is-open'));
      open.setAttribute('aria-expanded', 'true');
      this.releaseDrawer = Tc.modal(this.drawer, open, close);
    });
    close.addEventListener('click', () => this.closeDrawer());

    // One section open at a time; its photo sits inside the opened panel
    const accs = [...this.drawer.querySelectorAll('details[data-tc-acc]')];
    accs.forEach((d) => d.addEventListener('toggle', () => { if (d.open) accs.forEach((o) => { if (o !== d) o.open = false; }); }));
    open.addEventListener('click', () => this.drawer.querySelectorAll('[data-tc-sub-img]').forEach((i) => { i.loading = 'eager'; }), { once: true });
  }
  closeDrawer(restore = true) {
    if (this.drawer.hidden || this.drawer.inert) return;
    this.drawer.classList.remove('is-open'); this.drawer.inert = true;
    this.releaseDrawer?.(restore); this.releaseDrawer = null;
    this.querySelector('[data-tc-drawer-open]').setAttribute('aria-expanded', 'false');
    setTimeout(() => { if (!this.drawer.classList.contains('is-open')) { this.drawer.hidden = true; this.drawer.querySelectorAll('details[open]').forEach((d) => { d.open = false; }); } }, 600);
  }
  initSearch() {
    const openBtn = this.querySelector('[data-tc-search-open]');
    const input = this.querySelector('[data-tc-search-input]');
    const results = this.querySelector('[data-tc-search-results]');
    openBtn.addEventListener('click', () => {
      this.closePanels(); this.closeDrawer(false); this.search.inert = false;
      this.search.hidden = false; requestAnimationFrame(() => this.search.classList.add('is-open'));
      openBtn.setAttribute('aria-expanded', 'true');
      this.scrim.hidden = false; requestAnimationFrame(() => this.scrim.classList.add('is-on'));
      this.releaseSearch = Tc.modal(this.search, openBtn, input, [this.scrim]);
    });
    this.querySelector('[data-tc-search-close]').addEventListener('click', () => this.closeSearch());
    let timer, ctrl, requestId = 0;
    input.addEventListener('input', () => {
      clearTimeout(timer);
      ctrl?.abort();
      const id = ++requestId;
      const q = input.value.trim();
      results.innerHTML = '';
      results.removeAttribute('aria-busy');
      if (q.length < 2) return;
      results.setAttribute('aria-busy', 'true');
      timer = setTimeout(async () => {
        ctrl = new AbortController();
        try {
          const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
          const r = await fetch(`${root}search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,page,article&resources[limit]=6&resources[options][unavailable_products]=last`, { signal: ctrl.signal });
          if (!r.ok) throw new Error('search');
          const data = await r.json();
          if (id !== requestId || input.value.trim() !== q) return;
          const items = (data.resources && data.resources.results && data.resources.results.products) || [];
          const fmt = (v) => Tc.money(Math.round(Number(v) * 100));
          const esc = (t) => String(t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
          // Guides and info: the size guide for fit words, then any matching pages and articles
          const guides = [];
          if (results.dataset.sizeUrl && /\b(size|sizes|sizing|fit|fits|fitting|chart|measure|measurements|oz)\b/i.test(q)) guides.push({ title: results.dataset.sizeTitle || 'Size guide', url: results.dataset.sizeUrl });
          const res = (data.resources && data.resources.results) || {};
          [...(res.pages || []), ...(res.articles || [])].forEach((g) => { if (g.url && !guides.some((x) => x.url.split('?')[0] === g.url.split('?')[0])) guides.push({ title: g.title, url: g.url }); });
          const guideHTML = guides.length ? `<div class="tc-search__guides">${guides.slice(0, 4).map((g) => `<a href="${esc(g.url)}">${esc(g.title)}<svg width="14" height="10" viewBox="0 0 14 10" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M0 5h12M8 1l4 4-4 4"/></svg></a>`).join('')}</div>` : '';
          results.innerHTML = guideHTML + items.map((p) => `<a class="tc-search__hit" href="${esc(p.url)}"><figure>${p.image ? `<img src="${esc(p.image)}&width=400" alt="" loading="lazy">` : ''}</figure><p>${esc(p.title)}<br><span>${fmt(p.price)}</span></p></a>`).join('')
            + (items.length || guides.length ? '' : `<p class="tc-search__empty">${esc(results.dataset.empty)}</p>`)
            + `<a class="tc-link tc-search__all" href="${root}search?type=product&q=${encodeURIComponent(q)}">${esc(results.dataset.all)}</a>`;
        } catch (e) {
          if (id === requestId && e.name !== 'AbortError') results.textContent = results.dataset.error;
        } finally { if (id === requestId) results.removeAttribute('aria-busy'); }
      }, 180);
    });
  }
  closeSearch(restore = true) {
    if (this.search.hidden || this.search.inert) return;
    this.search.classList.remove('is-open'); this.search.inert = true;
    this.releaseSearch?.(restore); this.releaseSearch = null;
    this.querySelector('[data-tc-search-open]').setAttribute('aria-expanded', 'false');
    this.scrim.classList.remove('is-on');
    setTimeout(() => { if (!this.search.classList.contains('is-open')) { this.search.hidden = true; this.scrim.hidden = true; } }, 550);
  }
  closeAll() { this.closePanels(); this.closeDrawer(); this.closeSearch(); }
}
if (!customElements.get('tc-header')) customElements.define('tc-header', TcHeader);
